import React, { useState, useEffect, useCallback } from 'react';
import { apiKeyAPI } from '../services/api';
import { PageLayout } from './ui';

const ApiKeysPage = () => {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Create form
  const [showCreate, setShowCreate] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyScope, setNewKeyScope] = useState('register');
  const [creating, setCreating] = useState(false);

  // Newly created key (shown once)
  const [newRawKey, setNewRawKey] = useState(null);
  const [copied, setCopied] = useState(false);

  // Confirm revoke
  const [revokeId, setRevokeId] = useState(null);

  const loadKeys = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiKeyAPI.list();
      setKeys(data.api_keys || []);
    } catch (err) {
      setError(err.error || 'Failed to load API keys');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadKeys();
  }, [loadKeys]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newKeyName.trim()) return;

    try {
      setCreating(true);
      setError('');
      const data = await apiKeyAPI.create(newKeyName.trim(), newKeyScope);
      setNewRawKey(data.raw_key);
      setNewKeyName('');
      setNewKeyScope('register');
      setShowCreate(false);
      setCopied(false);
      loadKeys();
    } catch (err) {
      setError(err.error || 'Failed to create API key');
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (id) => {
    try {
      setError('');
      await apiKeyAPI.revoke(id);
      setSuccess('API key revoked');
      setRevokeId(null);
      loadKeys();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.error || 'Failed to revoke API key');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <PageLayout
      title="API Keys"
      subtitle="Manage API keys for external service integrations"
      backTo="/management"
      backLabel="Back to Management"
      actions={
        <button
          onClick={() => { setShowCreate(true); setNewRawKey(null); }}
          className="px-4 py-2 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary-hover"
        >
          + Create API Key
        </button>
      }
    >
      <div className="max-w-7xl mx-auto">

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
          {error}
          <button onClick={() => setError('')} className="ml-2 text-red-500 hover:text-red-700">&times;</button>
        </div>
      )}

      {success && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md text-green-700 text-sm">
          {success}
        </div>
      )}

      {/* New key reveal banner */}
      {newRawKey && (
        <div className="mb-6 p-4 bg-yellow-50 border border-yellow-300 rounded-lg">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="flex-1">
              <p className="text-sm font-semibold text-yellow-800 mb-2">
                Copy your API key now — it will not be shown again
              </p>
              <div className="flex items-center gap-2">
                <code className="flex-1 px-3 py-2 bg-white border border-yellow-200 rounded text-sm font-mono text-gray-800 break-all">
                  {newRawKey}
                </code>
                <button
                  onClick={() => copyToClipboard(newRawKey)}
                  className={`px-3 py-2 text-sm font-medium rounded-md whitespace-nowrap ${
                    copied
                      ? 'bg-green-100 text-green-700 border border-green-300'
                      : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {copied ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </div>
            <button
              onClick={() => setNewRawKey(null)}
              className="text-yellow-600 hover:text-yellow-800"
            >
              &times;
            </button>
          </div>
        </div>
      )}

      {/* Create form */}
      {showCreate && (
        <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Create New API Key</h3>
          <form onSubmit={handleCreate} className="flex items-end gap-3">
            <div className="flex-1">
              <label className="block text-xs text-gray-500 mb-1">Name (e.g. "Veeam Signup App")</label>
              <input
                type="text"
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
                placeholder="Enter a descriptive name"
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-primary focus:border-primary"
                autoFocus
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Scope</label>
              <select
                value={newKeyScope}
                onChange={(e) => setNewKeyScope(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-primary focus:border-primary"
              >
                <option value="register">register</option>
                <option value="v1">v1 (REST API)</option>
              </select>
            </div>
            <button
              type="submit"
              disabled={creating || !newKeyName.trim()}
              className="px-4 py-2 bg-primary text-white text-sm font-medium rounded-md hover:bg-primary-hover disabled:opacity-50"
            >
              {creating ? 'Creating...' : 'Create'}
            </button>
            <button
              type="button"
              onClick={() => setShowCreate(false)}
              className="px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
          </form>
        </div>
      )}

      {/* Keys table */}
      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading...</div>
      ) : keys.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <p className="text-lg">No API keys yet</p>
          <p className="text-sm mt-1">Create one to enable external service registration</p>
        </div>
      ) : (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Prefix</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Scopes</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Used</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {keys.map((k) => (
                <tr key={k.id} className={!k.is_active ? 'bg-gray-50 opacity-60' : ''}>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{k.name}</td>
                  <td className="px-4 py-3 text-sm font-mono text-gray-500">{k.key_prefix}...</td>
                  <td className="px-4 py-3 text-sm">
                    {(k.scopes || []).map((s) => (
                      <span key={s} className="inline-block px-2 py-0.5 bg-primary-light text-primary text-xs rounded mr-1">
                        {s}
                      </span>
                    ))}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {k.is_active ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">Active</span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">Revoked</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {k.last_used_at ? new Date(k.last_used_at).toLocaleDateString() : 'Never'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {k.created_at ? new Date(k.created_at).toLocaleDateString() : '—'}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {k.is_active && (
                      revokeId === k.id ? (
                        <div className="flex items-center justify-end gap-2">
                          <span className="text-xs text-red-600">Revoke?</span>
                          <button
                            onClick={() => handleRevoke(k.id)}
                            className="px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700"
                          >
                            Yes
                          </button>
                          <button
                            onClick={() => setRevokeId(null)}
                            className="px-2 py-1 text-xs border border-gray-300 text-gray-600 rounded hover:bg-gray-50"
                          >
                            No
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setRevokeId(k.id)}
                          className="text-sm text-red-600 hover:text-red-800"
                        >
                          Revoke
                        </button>
                      )
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Usage instructions */}
      <div className="mt-8 p-4 bg-gray-50 border border-gray-200 rounded-lg">
        <h3 className="text-sm font-semibold text-gray-700 mb-2">Usage</h3>
        <p className="text-sm text-gray-600 mb-3">
          External applications use the API key to register new users in the IDP:
        </p>
        <pre className="p-3 bg-gray-800 text-green-400 text-xs rounded-md overflow-x-auto">{`POST /api/register/external
Content-Type: application/json
X-API-Key: <your-api-key>

{
  "email": "client@example.com",
  "first_name": "John",
  "last_name": "Smith"
}

Response:
{
  "status": "success",
  "user": { "id": 42, "email": "client@example.com", ... },
  "reset_url": "https://clients.backups.net.au/reset-password?code=...&email=...",
  "zoho_sync": "synced"
}`}</pre>
        <p className="text-xs text-gray-500 mt-2">
          The endpoint creates the IDP account, syncs company/roles from Zoho, and returns a <strong>reset_url</strong> for your welcome email so the client can set their password.
          The reset link is valid for 72 hours.
        </p>
      </div>
      </div>
    </PageLayout>
  );
};

export default ApiKeysPage;
